<template>
  <fieldset class="fieldset">
    <legend v-if="label" class="fieldset-legend">{{ label }}</legend>

    <div class="relative">
      <input
        v-model="searchQuery"
        type="text"
        :placeholder="placeholder"
        class="input w-full"
        :class="{ 'input-error': error }"
        @input="handleInput"
        @blur="handleBlur"
        :required="required"
      />

      <!-- Suggestions dropdown -->
      <div
        v-if="suggestions.length > 0 && !isBlurred"
        class="absolute top-full left-0 z-10 w-full mt-1 bg-base-100 border rounded-md shadow-lg"
      >
        <div v-if="loading" class="p-2 text-base-content/60">Ieškoma...</div>
        <ul v-else class="max-h-60 overflow-auto">
          <li
            v-for="suggestion in suggestions"
            :key="suggestion.source + '-' + (suggestion.id ?? suggestion.name + suggestion.municipality)"
            @click="handleSelect(suggestion)"
            @mousedown="preventBlur"
            class="p-2 hover:bg-base-200 cursor-pointer flex items-center justify-between gap-2"
          >
            <span
              >{{ suggestion.name }}<template v-if="suggestion.municipality">
                ({{ suggestion.municipality }})</template
              ></span
            >
            <span
              v-if="suggestion.source === 'internal'"
              class="badge badge-sm badge-primary shrink-0"
              >Jūsų adresas</span
            >
          </li>
        </ul>
      </div>
    </div>

    <p v-if="error" class="fieldset-label text-error">{{ error }}</p>
  </fieldset>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from "vue";
import { useAddressSearch } from "@/composables/search/useAddressSearch";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  label: {
    type: String,
    default: "Adresas",
  },
  placeholder: {
    type: String,
    default: "Įveskite adresą...",
  },
  required: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue", "coordinates-change", "select"]);

const { suggestions, loading, searchAddress, selectAddress } =
  useAddressSearch();
const searchQuery = ref(props.modelValue);
const isBlurred = ref(false);

// Prevent dropdown from closing when clicking suggestion
const preventBlur = (e) => {
  e.preventDefault();
};

// Handle input changes
const handleInput = () => {
  emit("update:modelValue", searchQuery.value);
  searchAddress(searchQuery.value);
  isBlurred.value = false;
};

// Handle blur event
const handleBlur = () => {
  // Small timeout to allow click on suggestion to fire
  setTimeout(() => {
    isBlurred.value = true;
  }, 200);
};

// Handle suggestion selection
const handleSelect = (suggestion) => {
  const selected = selectAddress(suggestion);
  searchQuery.value = selected.address;
  emit("update:modelValue", selected.address);
  emit("coordinates-change", {
    lat: selected.lat,
    lng: selected.lng,
  });
  emit("select", {
    address: selected.address,
    lat: selected.lat,
    lng: selected.lng,
    raw: suggestion,
  });
  isBlurred.value = true;
};

// Watch for external value changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== searchQuery.value) {
      searchQuery.value = newValue;
    }
  },
);
</script>
